import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { ICommand, Rules, UserHeroCommand } from '@/types/heroCommands';
// import { fetchAssistantPrompt } from './useVapiCall';
import { useState, useRef, useEffect } from 'react';

interface UseCommandFormProps {
  variables: Record<string, any>;
  userCommand?: any;
}

export const useCommandForm = ({ variables, userCommand }: UseCommandFormProps) => {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [initialFormData, setInitialFormData] = useState<Record<string, string>>({});
  const [emptyFields, setEmptyFields] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<Record<string, HTMLTextAreaElement>>({});
  // console.log("formData", formData);
  useEffect(() => {
    try {
      setIsLoading(true);

      if (userCommand) {
        const initialData = Object.keys(variables).reduce((acc, key) => ({
          ...acc,
          [key]: userCommand.variables[key]?.value || ''
        }), {});
        setFormData(initialData);
        setInitialFormData(initialData);
      } else {
        const emptyData = Object.keys(variables).reduce((acc, key) => ({
          ...acc,
          [key]: ''
        }), {});
        setFormData(emptyData);
        setInitialFormData(emptyData);
      }
    } catch (err) {
      console.error('Error loading command data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [variables, userCommand]);

  const handleInputChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
    if (value.trim() && emptyFields.includes(key)) {
      setEmptyFields(prev => prev.filter(field => field !== key));
    }
  };

  const validateForm = (): boolean => {
    const empty = Object.entries(formData).reduce<string[]>((acc, [key, value]) => {
      if (!value.trim()) {
        acc.push(key);
      }
      return acc;
    }, []);

    setEmptyFields(empty);

    if (empty.length > 0 && empty[0] && inputRefs.current[empty[0]]) {
      inputRefs.current[empty[0]].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      inputRefs.current[empty[0]].focus();
    }

    return empty.length === 0;
  };

  const isFormValid = (): boolean => {
    return Object.entries(formData).every(([_, value]) => value.trim() !== '');
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setError(null);
    setEmptyFields([]);
  };

  const getModifiedFields = () => {
    return Object.keys(formData).filter(key => formData[key] !== initialFormData[key]);
  };

  return {
    formData,
    initialFormData,
    emptyFields,
    isLoading,
    error,
    inputRefs,
    handleInputChange,
    validateForm,
    isFormValid,
    resetForm,
    getModifiedFields,
    setError
  };
};

export const useAdvancedSettings = () => {
  const [selectedTool, setSelectedTool] = useState<string>('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customPrompt, setCustomPrompt] = useState<string>("");

  const toggleAdvanced = () => {
    const newShowAdvanced = !showAdvanced;
    setShowAdvanced(newShowAdvanced);
    if (!newShowAdvanced) {
      setSelectedTool('');
    }
  };

  const handleToolChange = (value: string) => {
    setSelectedTool(value);
    if (value !== 'manual_upload') {
      setCustomPrompt("");
    }
  };

  const resetSettings = () => {
    setSelectedTool('');
    setShowAdvanced(false);
    setCustomPrompt('');
  };

  return {
    selectedTool,
    showAdvanced,
    customPrompt,
    setCustomPrompt,
    toggleAdvanced,
    handleToolChange,
    resetSettings
  };
};
// Hook to fetch all hero commands
export const useHeroCommands = () => {
  return useQuery<ICommand[]>({
    queryKey: ['hero-commands'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hero_commands')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    refetchOnWindowFocus: false,
  });
};
// Hook to fetch user's hero commands
export const useUserHeroCommands = () => {
  return useQuery<UserHeroCommand[]>({
    queryKey: ['user-hero-commands'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_hero_commands')
        .select('*, hero_commands(prompt,rule,id)')
        .eq('user_id', session.user.id);

      if (error) throw error;

      return data || [];
    },
    refetchOnWindowFocus: false,
  });
};

// Hook to update user hero command
export const useUpdateUserHeroCommand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      heroCommandId,
      variables,
      isActive = true
    }: {
      heroCommandId: string;
      variables: Record<string, { value: string }>;
      isActive?: boolean;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get current user commands to check if command exists
      const { data: existingCommands } = await supabase
        .from('user_hero_commands')
        .select('*')
        .eq('user_id', user.id)
        .eq('hero_command_id', heroCommandId)
        .single();

      if (!existingCommands) {
        // Create new command
        const { data, error } = await supabase
          .from('user_hero_commands')
          .insert({
            hero_command_id: heroCommandId,
            user_id: user.id,
            is_active: isActive,
            variables
          })
          .select()
          .single<UserHeroCommand>();

        if (error) throw error;
        return data;
      } else {
        // Update existing command
        const { data, error } = await supabase
          .from('user_hero_commands')
          .update({
            variables,
            is_active: isActive,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingCommands.id)
          .select()
          .single<UserHeroCommand>();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      // Invalidate and refetch user hero commands
      queryClient.invalidateQueries({ queryKey: ['user-hero-commands'] });
      toast.success('Hero command updated successfully!');
    },
    onError: (err: Error) => {
      console.error('Error updating user command:', err);
      toast.error(`Failed to update command: ${err.message}`);
    },
  });
};

// Hook to toggle hero command status
export const useToggleHeroCommandStatus = () => {

  return useMutation({
    mutationFn: async ({
      commandId,
      newStatus
    }: {
      commandId: string;
      newStatus: boolean;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('user_hero_commands')
        .update({ is_active: newStatus })
        .eq('id', commandId)
        .select()
        .single<UserHeroCommand>();

      if (error) throw error;
      return data;
    },
    onError: (err: Error) => {
      console.error('Error toggling command status:', err);
      toast.error(`Failed to toggle command status: ${err.message}`);
    },
  });
};

export const useSyncVapiAssistant = () => {
  return useMutation({
    mutationFn: async (payload: {
      greeting: string;
      forwarding_number: string | null;
      voice_id: string;
      voice_provider: string;
      ai_name: string | null;
      business_name: string | null;
      hipaa_enabled: boolean;
      background_noise_enabled: boolean;
      inbound_first_message_mode: 'assistant-speaks-first' | 'assistant-waits-for-user';
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vapi-assistant-config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorBody = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(errorBody.error || `Failed to sync VAPI assistant (${res.status})`);
      }

      return await res.json();
    },
    onSuccess: () => {
      toast.success('AI Employee updated successfully!');
    },
    onError: (err: Error) => {
      console.error('Error syncing VAPI assistant:', err);
      toast.error(`Failed to sync AI: ${err.message}`);
    },
  });
};


export const useAssistantId = () => {
  return useQuery<string | null>({
    queryKey: ['assistant-id'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_settings')
        .select('vapi_assistant_id')
        .eq('user_id', session.user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data?.vapi_assistant_id || null;
    }
  });
};

export function useUpdateVapiPrompt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ assistantId }: { assistantId: string; }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('vapi-assistant-config', { body: {assistantId}});
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-hero-commands'] });
    },
  });
}

export const rules: Rules = {
  1: {
    "Core Outcome": "Core_Outcome",
    "Do Not Say Phrases": "Do_Not_Say_Phrases",
    "Key Inclusions": "Key_Inclusions",
    "Offer Name": "Offer_Name",
    "Program / Product / Service Name": "Program_Product_Service_Name",
    "Relevant Topic": "Relevant_Topic",
    "Target Audience": "Target_Audience",
    "Insert CTA": "Insert_CTA"
  },
  2: {
    "Program / Product / Service Name": "Program_Product_Service_Name",
    "Offer Name": "Offer_Name",
    "Target Audience": "Target_Audience",
    "Core Outcome": "Core_Outcome",
    "Pain Point": "Pain_Point",
    "Do Not Say Phrases": "Do_Not_Say_Phrases"
  },
  3: {
    "Offer / Service Name": "Offer_Service_Name",
    "Core Outcome": "Core_Outcome",
    "Do Not Say Phrases": "Do_Not_Say_Phrases"
  },
  4: {
    "Offer / Service Name": "Offer_Service_Name",
    "Target Audience": "Target_Audience",
    "Core Outcome": "Core_Outcome",
    "Do Not Say Phrases": "Do_Not_Say_Phrases"
  },
  5: {
    "Offer / Service Name": "Offer_Service_Name",
    "Do Not Say Phrases": "Do_Not_Say_Phrases"
  },
  6: {

    "Offer / Service Name": "Offer_Service_Name",
    "Review Type & Format": "Review_Type_Format",
    "Review Link": "Review_Link",
    "Do Not Say Phrases": "Do_Not_Say_Phrases"
  },
  7: {
    "Presented By": "Presented_By",
    "Webinar Name": "Webinar_Name",
    "Webinar Date": "Webinar_Date",
    "Webinar Label": "Webinar_Label",
    "Core Outcome": "Core_Outcome",
    "Target Audience": "Target_Audience",
    "Webinar Mechanism": "Webinar_Mechanism",
    "Example Result": "Example_Result",
    "VIP Offer Name": "VIP_Offer_Name",
    "VIP Benefits": "VIP_Benefits",
    "VIP Order Form URL": "VIP_Order_Form_URL",
    "Do Not Say Phrases": "Do_Not_Say_Phrases"
  },
  8: {
    "Webinar Name": "Webinar_Name",
    "Webinar Label": "Webinar_Label",
    "Webinar Date": "Webinar_Date",
    "Webinar Recap / Summary": "Webinar_Recap_Summary",
    "Program / Offer Name": "Program_Offer_Name",
    "Target Audience": "Target_Audience",
    "Core Outcome": "Core_Outcome",
    "Offer Mechanism / Strategy": "Offer_Mechanism_Strategy",
    "Offer Components": "Offer_Components",
    "Offer Investment": "Offer_Investment",
    "Urgency Reason": "Urgency_Reason",
    "Reschedule Link": "Reschedule_Link",
    "Do Not Say Phrases": "Do_Not_Say_Phrases",
    "Guarantee Terms": "Guarantee_Terms",

  }
};